import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { eachDayOfInterval } from 'date-fns/eachDayOfInterval'
import { isSameDay } from 'date-fns/isSameDay'
import { makeStyles } from 'tss-react/mui'
import FormControl from '@mui/material/FormControl'
import Grid from '@mui/material/Grid'
import InputLabel from '@mui/material/InputLabel'
import ListSubheader from '@mui/material/ListSubheader'
import MenuItem from '@mui/material/MenuItem'
import type { SelectChangeEvent } from '@mui/material/Select'
import Select from '@mui/material/Select'

import { ArrowButtonWithText } from 'src/Components/ArrowButtonWithText'
import { SwingDirectionIcon } from './SwingDirectionIcon'

import type { SwingEntry, SwingStatus } from 'src/Types/SwingEntry'
import type { AssetStatusWindow, WindowStatus } from 'src/Types/AssetTypes'
import { WindowStatusValue } from 'src/Types/AssetTypes'

import { formatLocalDateTime } from 'src/Utils/format'
import { useBreakpointDown } from 'src/Hooks/useBreakpoint'
import { findActiveStatusWindow } from 'src/Utils/statusWindow'
import themeColors from 'src/theme'
import { isWindowStatus } from 'src/Utils/asset'
import { isAlarmClassification } from 'src/Utils/alarms'
import { AlarmClassificationValue } from 'src/Types/AlarmClassification'

const stripedBackground = (mainColor: string, stripeColor: string) => {
  return `repeating-linear-gradient(
    -55deg,
    ${mainColor},
    ${mainColor} 27px,
    ${stripeColor} 15px,
    ${stripeColor} 30px
  )`
}

const useStyles = makeStyles()(theme => ({
  swingArrows: {
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      paddingLeft: 8,
      paddingRight: 8,
    },
  },
  currentSwingDate: {
    textAlign: 'center',
    fontSize: 12,
    fontWeight: 'bold',
  },
  dropDownFormControl: {
    marginBottom: theme.spacing(1),
    width: '100%',
  },
  listHeader: {
    backgroundColor: '#fff',
    color: themeColors.secondary,
  },
  warningEntry: {
    backgroundColor: `${themeColors.warningBackground} !important`,
  },
  alarmEntry: {
    backgroundColor: `${themeColors.errorBackground} !important`,
  },
  noContactEntry: {
    backgroundColor: `${themeColors.noContactSemiLight} !important`,
  },
  noContactEntryAlarm: {
    background: stripedBackground(themeColors.noContactSemiLight, themeColors.error),
  },
  noContactEntryWarning: {
    background: stripedBackground(themeColors.noContactSemiLight, themeColors.warning),
  },
  irregularDataEntry: {
    backgroundColor: `${themeColors.irregularData} !important`,
  },
  irregularDataEntryAlarm: {
    background: stripedBackground(themeColors.irregularData, themeColors.error),
  },
  irregularDataEntryWarning: {
    background: stripedBackground(themeColors.irregularData, themeColors.warning),
  },
  currentEntry: {
    fontWeight: 'bold',
  },
  swingDirectionIcon: {
    verticalAlign: 'bottom',
    marginRight: 4,
  },
  swingDate: {
    flex: 1,
    textAlign: 'left',
  },
  swingDateIrregularData: {
    flex: 1,
    textAlign: 'left',
    color: `${themeColors.irregularDataText} !important`,
  },
  swingDateNoContact: {
    flex: 1,
    textAlign: 'left',
    color: `${themeColors.noContactText} !important`,
  },
  swingDirectionText: {
    marginRight: 4,
  },
  swingDirectionTextIrregularData: {
    color: themeColors.irregularDataText,
  },
  swingDirectionTextNoContact: {
    color: themeColors.noContactText,
  },
}))

type StyleKeys = keyof ReturnType<typeof useStyles>['classes']

const hardCodedSwingStatus: WindowStatus | undefined | null = null
const hardCodedRealSwingStatus: SwingStatus | undefined | null = null

const getSwingElementClass = (status: SwingEntry['status'], realStatus?: SwingEntry['status']): StyleKeys | undefined => {
  if (hardCodedSwingStatus !== null) {
    status = hardCodedSwingStatus
  }
  if (hardCodedRealSwingStatus !== null) {
    realStatus = hardCodedRealSwingStatus
  }

  switch (status) {
    case AlarmClassificationValue.Alarm:
      return 'alarmEntry'

    case AlarmClassificationValue.Warning:
      return 'warningEntry'

    case WindowStatusValue.Maintenance:
    case WindowStatusValue.Out_of_Order:
    case WindowStatusValue.NoContact:
      return isAlarmClassification(realStatus) ? `noContactEntry${realStatus}` : 'noContactEntry'

    case WindowStatusValue.IrregularData:
      return isAlarmClassification(realStatus) ? `irregularDataEntry${realStatus}` : 'irregularDataEntry'

    default:
      return undefined
  }
}

const getDateClass = (swingStatus: SwingStatus, classes: Record<string, string>) => {
  if (isWindowStatus(swingStatus)) {
    switch (swingStatus) {
      case WindowStatusValue.IrregularData:
        return classes.swingDateIrregularData

      case WindowStatusValue.Maintenance:
      case WindowStatusValue.Out_of_Order:
      case WindowStatusValue.NoContact:
        return classes.swingDateNoContact

      default:
    }
  }
  return classes.swingDate
}

const getIconColor = (swingStatus: SwingStatus) => {
  if (isWindowStatus(swingStatus)) {
    switch (swingStatus) {
      case WindowStatusValue.IrregularData:
        return themeColors.irregularDataText

      case WindowStatusValue.Maintenance:
      case WindowStatusValue.Out_of_Order:
      case WindowStatusValue.NoContact:
        return themeColors.noContactText

      default:
        return themeColors.disabledLight
    }
  }
  return undefined
}

const getSelectClass = (swing: SwingEntry, classes: Record<string, string>) => {
  const selectClassKey = getSwingElementClass(swing.status, swing.realStatus)
  return selectClassKey ? classes[selectClassKey] : undefined
}

type OwnProps = {
  swings: SwingEntry[]
  selectedSwingTimestamp: number
  isRefetching: boolean
  statusWindows?: AssetStatusWindow[]
  onSwingChanged: (swingId: number) => void
}

// This fetches the three relevant swings (previous, current, next), but needs to guard
// against a negative index in the slice by prepending an undefined element.
const getRelevantSwings = (swings: SwingEntry[], currentIndex: number) =>
  [undefined, ...swings].slice(currentIndex, currentIndex + 3)

export const SwingSelector = ({ swings, selectedSwingTimestamp, isRefetching, statusWindows, onSwingChanged }: OwnProps) => {
  const { t } = useTranslation()
  const { classes } = useStyles()
  const narrowDisplay = useBreakpointDown('md')
  const isMediaLG = useBreakpointDown('lg')

  const swingsWithStatus = useMemo(
    () =>
      swings.map<SwingEntry>(s => {
        const activeWindow = findActiveStatusWindow(statusWindows, s.timestamp)
        const status = activeWindow ? activeWindow.status : s.status

        return {
          ...s,
          status,
          realStatus: s.status,
        }
      }),
    [statusWindows, swings]
  )

  const groupedSwings = useMemo(
    () =>
      swingsWithStatus.length
        ? eachDayOfInterval({
            start: swingsWithStatus[0].timestamp,
            end: swingsWithStatus[swingsWithStatus.length - 1].timestamp,
          }).map(date => {
            const daySwings = swingsWithStatus.filter(s => isSameDay(s.timestamp, date))

            return {
              date,
              swings: daySwings,
            }
          })
        : [],
    [swingsWithStatus]
  )

  const arrowText = (translationKey: string, swing?: SwingEntry) => {
    if (!swing) {
      return t(translationKey)
    }

    return !narrowDisplay ? formatLocalDateTime(swing.timestamp) : ''
  }

  const swingChanged = (event: SelectChangeEvent<string>) => {
    const swing = swingsWithStatus.find(swing => swing.swingId === event.target.value)
    if (swing) {
      onSwingChanged(swing.timestamp)
    }
  }

  const selectedIndex = swings.findIndex(swing => swing.timestamp === selectedSwingTimestamp)
  const [prevSwing, currentSwing, nextSwing] = getRelevantSwings(swingsWithStatus, selectedIndex)

  if (hardCodedSwingStatus && currentSwing) {
    currentSwing.status = hardCodedSwingStatus
  }
  if (hardCodedRealSwingStatus && currentSwing) {
    currentSwing.realStatus = hardCodedRealSwingStatus
  }

  if (!currentSwing) {
    return null
  }

  return (
    <Grid container className={classes.swingArrows}>
      <Grid item xs={narrowDisplay ? 2 : 3}>
        {prevSwing && (
          <ArrowButtonWithText
            text={arrowText(isMediaLG ? 'switches.prevSwing_short' : 'switches.prevSwing', prevSwing)}
            direction="left"
            onClick={() => onSwingChanged(prevSwing.timestamp)}
          />
        )}
      </Grid>
      <Grid item xs={narrowDisplay ? 8 : 6} className={classes.currentSwingDate}>
        <FormControl className={classes.dropDownFormControl}>
          <InputLabel id="swing-selector-label">{t('switches.statusLabels.time')}</InputLabel>
          <Select
            labelId="swing-selector-label"
            id="swing-selector"
            value={currentSwing?.swingId}
            onChange={swingChanged}
            className={getSelectClass(currentSwing, classes)}
          >
            {groupedSwings.map(day => [
              <ListSubheader className={classes.listHeader} color="primary">
                {formatLocalDateTime(day.date, true)}
              </ListSubheader>,
              day.swings.map(swing => {
                if (hardCodedSwingStatus !== null) {
                  swing.status = hardCodedSwingStatus
                }
                if (hardCodedRealSwingStatus !== null) {
                  swing.realStatus = hardCodedRealSwingStatus
                }

                const direction = swing.direction
                const directionLabel = t(`switches.statusLabels.direction.${direction}`)
                const itemClassKey = getSwingElementClass(swing.status, swing.realStatus)
                const itemClass = itemClassKey ? classes[itemClassKey] : undefined

                return (
                  <MenuItem
                    key={swing.swingId}
                    value={swing.swingId}
                    className={clsx(itemClass, { [classes.currentEntry]: swing.timestamp === selectedSwingTimestamp })}
                    style={{ display: 'flex' }}
                  >
                    <span className={getDateClass(swing.status, classes)}>{formatLocalDateTime(swing.timestamp)}</span>
                    {!narrowDisplay && (
                      <span
                        className={clsx(classes.swingDirectionText, {
                          [classes.swingDirectionTextIrregularData]: swing.status === WindowStatusValue.IrregularData,
                          [classes.swingDirectionTextNoContact]: swing.status === WindowStatusValue.NoContact,
                        })}
                      >
                        {directionLabel}
                      </span>
                    )}
                    <SwingDirectionIcon
                      direction={direction}
                      className={classes.swingDirectionIcon}
                      color={getIconColor(swing.status)}
                      title={directionLabel}
                    />
                  </MenuItem>
                )
              }),
            ])}
          </Select>
        </FormControl>
      </Grid>
      <Grid container item xs={narrowDisplay ? 2 : 3} alignContent="flex-end" flexDirection="column">
        <ArrowButtonWithText
          text={arrowText(isMediaLG ? 'switches.nextSwing_short' : 'switches.nextSwing', nextSwing)}
          direction="right"
          disabled={!nextSwing}
          isRefetching={isRefetching}
          onClick={() => {
            if (nextSwing) {
              onSwingChanged(nextSwing.timestamp)
            }
          }}
        />
      </Grid>
    </Grid>
  )
}
